/*
  RESADE Ankündigung (sections/rs-ankuendigung.liquid, Feedback Runde 1 vom 25.09.2026, Abschnitt 1).
  Custom Element <rs-ankuendigung>. Zeigt immer eine Botschaft und wechselt automatisch alle
  data-sekunden Sekunden (Standard 5).

  - Pfeile (data-rs-ak-zurueck, data-rs-ak-weiter) und waagerechtes Wischen wechseln von Hand. Wer
    selbst wechselt, hält den automatischen Wechsel für diesen Seitenaufruf an: Der Text läuft dann
    nicht mehr weg, während jemand liest.
  - Pause, solange die Maus über der Leiste steht, der Fokus in ihr liegt, ein Finger sie berührt,
    der Tab verdeckt ist oder im Theme-Editor eine Botschaft gewählt ist.
  - Bei prefers-reduced-motion und html.ohne-bewegung (Theme-Einstellung rs_bewegung_an aus)
    wechselt nichts von selbst; die Pfeile bleiben.
  - aria-live="polite" nur beim Wechsel durch den Nutzer, beim automatischen Wechsel "off".
  - Nicht gezeigte Botschaften tragen aria-hidden und sind per CSS visibility: hidden (nicht fokussierbar).
*/
(() => {
  if (!window.customElements || customElements.get('rs-ankuendigung')) return;

  const ruhig = window.matchMedia('(prefers-reduced-motion: reduce)');
  const WISCH_MIN = 40;

  class RsAnkuendigung extends HTMLElement {
    connectedCallback() {
      this.botschaften = Array.from(this.querySelectorAll('[data-rs-ak-botschaft]'));
      this.fenster = this.querySelector('[data-rs-ak-fenster]');
      if (this.botschaften.length < 2 || !this.fenster) return;

      this.index = Math.max(0, this.botschaften.findIndex((b) => b.classList.contains('ist-aktiv')));
      const s = parseFloat(this.dataset.sekunden);
      this.sekunden = Number.isFinite(s) && s >= 3 ? s : 5;
      this.auto = this.dataset.auto !== 'false';
      this.gestoppt = false;
      this.pausen = new Set();
      this.timer = null;
      this.start = null;

      this.ab = new AbortController();
      const opt = { signal: this.ab.signal };
      const passiv = { signal: this.ab.signal, passive: true };

      const zurueck = this.querySelector('[data-rs-ak-zurueck]');
      const weiter = this.querySelector('[data-rs-ak-weiter]');
      if (zurueck) zurueck.addEventListener('click', () => this.vonHand(-1), opt);
      if (weiter) weiter.addEventListener('click', () => this.vonHand(1), opt);

      // Zeigen nur mit echter Maus; Touch meldet pointerenter ebenfalls, das regelt die Berührung
      this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.pause('zeiger', true); }, opt);
      this.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') this.pause('zeiger', false); }, opt);
      this.addEventListener('focusin', () => this.pause('fokus', true), opt);
      this.addEventListener('focusout', (e) => {
        if (!e.relatedTarget || !this.contains(e.relatedTarget)) this.pause('fokus', false);
      }, opt);

      // Berührung und Wischen
      this.addEventListener('touchstart', (e) => {
        this.pause('beruehrung', true);
        if (e.touches.length !== 1) { this.start = null; return; }
        this.start = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }, passiv);
      this.addEventListener('touchend', (e) => {
        const t = e.changedTouches && e.changedTouches[0];
        if (this.start && t && this.fenster.contains(e.target)) {
          const dx = t.clientX - this.start.x;
          const dy = t.clientY - this.start.y;
          if (Math.abs(dx) >= WISCH_MIN && Math.abs(dx) > Math.abs(dy) * 1.2) this.vonHand(dx < 0 ? 1 : -1);
        }
        this.start = null;
        this.pause('beruehrung', false);
      }, passiv);
      this.addEventListener('touchcancel', () => { this.start = null; this.pause('beruehrung', false); }, passiv);

      document.addEventListener('visibilitychange', () => this.pause('verdeckt', document.hidden), opt);
      if (ruhig.addEventListener) ruhig.addEventListener('change', () => this.planen(), opt);

      // Theme-Editor: gewählte Botschaft zeigen und stehen lassen
      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          const i = this.botschaften.indexOf(e.target);
          if (i < 0) return;
          this.pause('editor', true);
          this.zeigen(i, i < this.index ? -1 : 1, false);
        }, opt);
        document.addEventListener('shopify:block:deselect', (e) => {
          if (this.botschaften.indexOf(e.target) >= 0) this.pause('editor', false);
        }, opt);
      }

      this.classList.add('ist-bereit');
      this.planen();
    }

    disconnectedCallback() {
      if (this.ab) this.ab.abort();
      clearTimeout(this.timer);
      this.timer = null;
    }

    bewegungAus() {
      return ruhig.matches || document.documentElement.classList.contains('ohne-bewegung');
    }

    darfLaufen() {
      return this.auto && !this.gestoppt && this.pausen.size === 0 && !this.bewegungAus();
    }

    planen() {
      clearTimeout(this.timer);
      this.timer = null;
      if (!this.darfLaufen()) return;
      this.timer = setTimeout(() => this.zeigen(this.index + 1, 1, false), this.sekunden * 1000);
    }

    pause(grund, an) {
      if (an) this.pausen.add(grund);
      else this.pausen.delete(grund);
      this.planen();
    }

    vonHand(schritt) {
      this.gestoppt = true;
      this.zeigen(this.index + schritt, schritt, true);
    }

    zeigen(ziel, richtung, vomNutzer) {
      const n = this.botschaften.length;
      const neu = ((ziel % n) + n) % n;
      if (neu !== this.index) {
        this.fenster.setAttribute('aria-live', vomNutzer ? 'polite' : 'off');
        this.style.setProperty('--rs-ak-dx', richtung < 0 ? '-1' : '1');
        const alt = this.botschaften[this.index];
        const jetzt = this.botschaften[neu];
        // Startlage der übrigen Botschaften ohne Übergang auf die neue Richtung setzen, sonst gleitet
        // eine früher ausgeblendete Botschaft von der falschen Seite herein
        this.botschaften.forEach((b) => {
          if (b === alt) return;
          b.style.transition = 'none';
          b.classList.remove('ist-weg');
        });
        void jetzt.offsetWidth;
        this.botschaften.forEach((b) => { if (b !== alt) b.style.transition = ''; });
        alt.classList.remove('ist-aktiv');
        alt.classList.add('ist-weg');
        alt.setAttribute('aria-hidden', 'true');
        // Lighthouse 18.10.2026: während der 300 ms Ausblenden ist der Link sonst fokussierbar (aria-hidden-focus)
        alt.inert = true;
        jetzt.classList.add('ist-aktiv');
        jetzt.removeAttribute('aria-hidden');
        jetzt.inert = false;
        this.index = neu;
      }
      this.planen();
    }
  }

  customElements.define('rs-ankuendigung', RsAnkuendigung);
})();
